import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { formatAgeLimit, parseAgeLimit } from "../../utils/ageLimit";

const EditTestModal = () => {
  const { testId } = useParams();
  const navigate = useNavigate();

  // form states
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [parameters, setParameters] = useState([]);
  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [report, setReport] = useState("");
  const [fasting, setFasting] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [gstSlab, setGstSlab] = useState("0");
  const [recommendedGender, setRecommendedGender] = useState("Both");
  const [sampleType, setSampleType] = useState("");
  const [testCriteria, setTestCriteria] = useState("");
  const [testInstruction, setTestInstruction] = useState("");

  // ui states
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [saveMessage, setSaveMessage] = useState("");

  /* =========================
     FETCH TEST DATA
  ========================== */
  useEffect(() => {
    if (!testId) return;

    const fetchTest = async () => {
      try {
        setLoading(true);

        const response = await AuthInterceptor(`/admin/test/${testId}`, {
          method: "GET",
        });

        const data = response.data;

        if (data?.s === 200 && data?.r) {
          const test = data.r;

          setTitle(test.title ?? "");
          setDescription(test.description ?? "");
          setParameters(test.parameters?.map((p) => p.name) ?? []);
          const age = parseAgeLimit(test.ageLimit);
          setAgeFrom(age.from);
          setAgeTo(age.to);
          setReport(test.report ?? "");
          setFasting(
            typeof test.fasting === "boolean" ? String(test.fasting) : (test.fasting ?? "")
          );
          setOriginalPrice(test.originalPrice ?? "");
          setDiscountedPrice(test.discountedPrice ?? "");
          setDiscountPercent(test.discountPercent ?? "");
          setGstSlab(String(test.gstSlab ?? test.gst ?? 0));
          setRecommendedGender(test.recommendedGender ?? "All");
          setSampleType(test.sampleType ?? "");
          setTestCriteria(test.testCriteria ?? "");
          setTestInstruction(test.testInstruction ?? "");
        } else {
          throw new Error(data?.m || "Failed to load test data");
        }
      } catch (error) {
        setSnack({
          notification: {
            message: error.message || "Error loading test",
            type: "error",
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [testId]);

  useEffect(() => {
    const mrp = Number(originalPrice);
    const selling = Number(discountedPrice);
    if (mrp > 0 && selling >= 0 && selling <= mrp) {
      setDiscountPercent(String(Math.round(((mrp - selling) / mrp) * 100)));
    } else {
      setDiscountPercent("");
    }
  }, [originalPrice, discountedPrice]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const body = {
        title,
        description,
        parameters: parameters.map((p) => ({ name: p })),
        ageLimit: formatAgeLimit(ageFrom, ageTo),
        report,
        fasting,
        originalPrice,
        discountedPrice,
        discountPercent,
        gstSlab,
        recommendedGender,
        sampleType,
        testCriteria,
        testInstruction,
      };

      const response = await AuthInterceptor(`/admin/test/${testId}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });

      const data = response.data;

      if (data?.s === 200 || data?.s === 201) {
        setSnack({
          notification: {
            message: "Test updated successfully",
            type: "success",
          },
        });
        setSaveMessage("Changes saved");

        setTimeout(() => navigate(-1), 1200);
      } else {
        throw new Error(data?.m || "Failed to update test");
      }
    } catch (error) {
      setSnack({
        notification: {
          message: error.message || "Error updating test",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}

      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="container mt-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Edit Test</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Title</label>
                <input
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Age Limit</label>
                <div className="input-group">
                  <input type="number" min="0" max="150" className="form-control" placeholder="From" value={ageFrom} onChange={(e) => setAgeFrom(e.target.value)} />
                  <span className="input-group-text">to</span>
                  <input type="number" min="0" max="150" className="form-control" placeholder="To" value={ageTo} onChange={(e) => setAgeTo(e.target.value)} />
                </div>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">
                  Parameters (comma separated)
                </label>
                <input
                  className="form-control"
                  value={parameters.join(", ")}
                  onChange={(e) =>
                    setParameters(
                      e.target.value.split(",").map((v) => v.trim())
                    )
                  }
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Report</label>
                <input
                  className="form-control"
                  value={report}
                  onChange={(e) => setReport(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Sample Type</label>
                <input
                  className="form-control"
                  value={sampleType}
                  onChange={(e) => setSampleType(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">MRP (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={originalPrice}
                  onChange={(e) => setOriginalPrice(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discounted Price (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={discountedPrice}
                  onChange={(e) => setDiscountedPrice(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Recommended Gender</label>
                <select
                  className="form-select"
                  value={recommendedGender}
                  onChange={(e) => setRecommendedGender(e.target.value)}
                >
                  <option value="Both">Both</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Fasting Required</label>
                <input
                  type="text"
                  className="form-control"
                  value={fasting}
                  onChange={(e) => setFasting(e.target.value)}
                  placeholder="Enter fasting requirement"
                />
                <div className="text-muted small mt-1">
                  Backend expects a boolean value for this field.
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discount Percent (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={discountPercent}
                  readOnly
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">GST Slab</label>
                <select
                  className="form-select"
                  value={gstSlab}
                  onChange={(e) => setGstSlab(e.target.value)}
                >
                  <option value="0">GST 0%</option>
                  <option value="5">GST 5%</option>
                  <option value="18">GST 18%</option>
                </select>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Test Instruction</label>
                <textarea
                  className="form-control"
                  value={testInstruction}
                  onChange={(e) => setTestInstruction(e.target.value)}
                />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Note</label>
                <textarea
                  className="form-control"
                  value={testCriteria}
                  onChange={(e) => setTestCriteria(e.target.value)}
                />
              </div>
            </div>

            <div className="text-end">
              {saveMessage && <span className="text-success me-3">{saveMessage}</span>}
              <button
                className="btn btn-outline-custom"
                type="submit"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Test"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditTestModal;
